import React, { useState, useEffect } from "react";
import { Link } from 'react-router-dom';
import {
  Typography, Box, Grid, CircularProgress, Paper, Button,
  Stack, Chip, IconButton, Dialog, DialogTitle, 
  DialogContent, List, ListItem, ListItemText, ListItemAvatar, 
  Avatar, Divider, Tabs, Tab, TextField, InputAdornment
} from "@mui/material";
import { styled } from "@mui/system";
import { db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { format } from "date-fns";
import { it } from 'date-fns/locale';

// Icone
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import EventBusyIcon from '@mui/icons-material/EventBusy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaidIcon from '@mui/icons-material/Paid';
import SearchIcon from '@mui/icons-material/Search'; // MODIFICA: Nuova icona ricerca
import ClearIcon from '@mui/icons-material/Clear';   // MODIFICA: Icona per pulire il campo

// --- STYLED COMPONENTS ---
const MainContainer = styled(Box)({
  minHeight: "100vh",
  background: "#F8F9FA",
  padding: "30px",
  fontFamily: "'Inter', sans-serif",
  color: "#333",
});

const ModernCard = styled(Paper)(({ theme, isAlert, isClickable }) => ({
  background: isAlert ? "#FFF5F5" : "#FFFFFF",
  borderRadius: "24px",
  border: "none",
  boxShadow: "0px 10px 40px rgba(0,0,0,0.04)",
  padding: "24px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  transition: "all 0.2s ease-in-out",
  cursor: isClickable ? "pointer" : "default",
  "&:hover": {
    transform: isClickable ? "translateY(-4px)" : "none",
    boxShadow: isClickable ? "0px 15px 50px rgba(0,0,0,0.08)" : "0px 10px 40px rgba(0,0,0,0.04)",
  }
}));

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: "1.1rem",
  color: "#2d3436",
  marginBottom: "16px",
  letterSpacing: "-0.5px"
});

const StatValue = styled(Typography)({
  fontWeight: 800,
  background: "linear-gradient(135deg, #6C3483 0%, #a32b2b 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  fontSize: "2.8rem",
  lineHeight: 1.2,
  marginBottom: "8px"
});

export default function Dashboard() {
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [expiredCertificates, setExpiredCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Stati per il Popup, navigazione Tab e Ricerca
  const [openPopup, setOpenPopup] = useState(false);
  const [popupType, setPopupType] = useState(""); // 'subs' | 'certs'
  const [tabIndex, setTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState(""); // MODIFICA: Stato per la barra di ricerca

  const todayObj = new Date();
  const todayStr = format(todayObj, 'yyyy-MM-dd');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const usersSnapshot = await getDocs(collection(db, "users"));
        const usersData = usersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsers(usersData);

        const bookingsSnapshot = await getDocs(collection(db, "userBookings"));
        const bookingsData = bookingsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setBookings(bookingsData);

        const certsSnapshot = await getDocs(collection(db, "userCertificates"));
        const now = new Date();
        const expiredCerts = certsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(cert => {
            const exp = cert.expiresAt.toDate ? cert.expiresAt.toDate() : new Date(cert.expiresAt);
            return exp < now;
          });
        setExpiredCertificates(expiredCerts);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // --- LOGICA FILTRI UTENTI E ABBONAMENTI ---
  const recentUsersCount = users.filter(u => {
    if (!u.createdAt) return false;
    const d = u.createdAt.toDate ? u.createdAt.toDate() : new Date(u.createdAt);
    return d > (new Date(Date.now() - 30 * 24 * 60 * 60 * 1000));
  }).length;

  const filteredBookings = bookings.filter(b => b.dataPrenotazione === todayStr);

  const getExpiryDate = (u) => {
    if (!u.abbonamentoScadenza) return null;
    return u.abbonamentoScadenza.toDate ? u.abbonamentoScadenza.toDate() : new Date(u.abbonamentoScadenza);
  };

  const now = new Date();
  const nextWeek = new Date();
  nextWeek.setDate(now.getDate() + 7);

  // 1. In scadenza nei prossimi 7 giorni
  const expiringUsers = users.filter(u => {
    const expiry = getExpiryDate(u);
    return expiry && expiry >= now && expiry < nextWeek; 
  });

  // 2. Abbonamenti regolarmente attivi
  const activeUsers = users.filter(u => {
    const expiry = getExpiryDate(u);
    const hasIngressi = u.tipoAbbonamento === 'Carnet' && (u.ingressiResidui > 0);
    return (expiry && expiry >= now) || hasIngressi;
  });

  // 3. Abbonamenti scaduti che NON hanno rinnovato
  const expiredUsers = users.filter(u => {
    const expiry = getExpiryDate(u);
    if (!expiry) return false;
    const isDateExpired = expiry < now;
    const noIngressiLeft = u.tipoAbbonamento !== 'Carnet' || (u.ingressiResidui <= 0 || !u.ingressiResidui);
    return isDateExpired && noIngressiLeft;
  });

  const handleOpenDetails = (type) => {
    setPopupType(type);
    setTabIndex(0);
    setSearchQuery(""); // MODIFICA: Resetta la ricerca quando si apre una nuova modale
    setOpenPopup(true);
  };

  if (loading) return (
    <Box sx={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: '#a32b2b' }} />
    </Box>
  );

  return (
    <MainContainer>
      {/* HEADER */}
      <Box sx={{ mb: 5, display: 'flex', flexDirection: {xs:'column', md:'row'}, justifyContent: 'space-between', alignItems: {xs:'flex-start', md:'center'}, gap: 2 }}>
        <Box>
           <Typography variant="h4" fontWeight="800" sx={{ color: "#2d3436" }}>
             Bentornato, Admin
           </Typography>
           <Typography variant="body1" sx={{ color: '#636e72', mt: 0.5 }}>
             Ecco la situazione in tempo reale.
           </Typography>
        </Box>

        <Paper elevation={0} sx={{
            display: 'flex', alignItems: 'center', gap: 2,
            padding: '12px 24px', borderRadius: '20px',
            bgcolor: '#fff', boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
        }}>
            <CalendarMonthIcon sx={{ color: '#a32b2b', fontSize: 30 }} />
            <Box>
                <Typography variant="caption" display="block" sx={{ color: '#888', fontWeight: 'bold', letterSpacing: 1 }}>
                    OGGI
                </Typography>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#2d3436', textTransform: 'capitalize', lineHeight: 1 }}>
                    {format(todayObj, 'd MMMM yyyy', { locale: it })}
                </Typography>
            </Box>
        </Paper>
      </Box>

      {/* 1. SEZIONE KPI */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        
        {/* Utenti */}
        <Grid item xs={12} sm={6} md={3}>
          <ModernCard>
            <Box>
                <Typography variant="subtitle2" sx={{ color: '#b2bec3', fontWeight: 600, textTransform: 'uppercase' }}>ISCRITTI TOTALI</Typography>
                <StatValue>{users.length}</StatValue>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#e8f5e9', width: 'fit-content', px: 1, py: 0.5, borderRadius: 2 }}>
                <TrendingUpIcon fontSize="small" sx={{ mr: 0.5, color: '#2e7d32' }} />
                <Typography variant="caption" fontWeight="bold" sx={{ color: '#2e7d32' }}>+{recentUsersCount} nuovi (30gg)</Typography>
            </Box>
          </ModernCard>
        </Grid>

        {/* Prenotazioni Giorno */}
        <Grid item xs={12} sm={6} md={3}>
          <ModernCard>
            <Box>
                <Typography variant="subtitle2" sx={{ color: '#b2bec3', fontWeight: 600, textTransform: 'uppercase' }}>PRENOTAZIONI OGGI</Typography>
                <StatValue>{filteredBookings.length}</StatValue>
            </Box>
             <Chip label="Agenda Attiva" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold', width: 'fit-content' }} />
          </ModernCard>
        </Grid>

        {/* ABBONAMENTI */}
        <Grid item xs={12} sm={6} md={3}>
          <ModernCard 
            isClickable 
            onClick={() => handleOpenDetails("subs")}
          >
             <Box>
                <Typography variant="subtitle2" sx={{ color: '#b2bec3', fontWeight: 600, textTransform: 'uppercase' }}>GESTIONE ABBONAMENTI</Typography>
                <StatValue sx={{ background: "linear-gradient(135deg, #f0932b 0%, #eb4d4b 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                    {expiringUsers.length} <Typography component="span" variant="caption" sx={{ color: '#eb4d4b', fontWeight: 'bold', fontSize: '0.9rem' }}>in scad. (7gg)</Typography>
                </StatValue>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#636e72', pt: 1, borderTop: '1px solid #f1f2f6' }}>
                <Typography variant="caption" fontWeight="bold">Attivi: {activeUsers.length}</Typography>
                <Typography variant="caption" fontWeight="bold" sx={{ color: expiredUsers.length > 0 ? '#d32f2f' : 'inherit' }}>Scaduti: {expiredUsers.length}</Typography>
            </Box>
          </ModernCard>
        </Grid>

        {/* Certificati Scaduti */}
        <Grid item xs={12} sm={6} md={3}>
          <ModernCard 
            isAlert={expiredCertificates.length > 0} 
            isClickable={expiredCertificates.length > 0}
            onClick={() => {
                if(expiredCertificates.length === 0) return;
                handleOpenDetails("certs");
            }}
          >
             <Box>
                <Typography variant="subtitle2" sx={{ color: expiredCertificates.length > 0 ? '#d32f2f' : '#b2bec3', fontWeight: 600, textTransform: 'uppercase' }}>
                    CERTIFICATI SCADUTI
                </Typography>
                <StatValue sx={{ 
                    color: expiredCertificates.length > 0 ? "#c62828" : "inherit",
                    WebkitTextFillColor: expiredCertificates.length > 0 ? "#c62828" : "transparent"
                }}>
                    {expiredCertificates.length}
                </StatValue>
            </Box>
            {expiredCertificates.length > 0 ? (
                 <Box sx={{ display: 'flex', alignItems: 'center', color: '#c62828' }}>
                    <WarningAmberIcon fontSize="small" sx={{ mr: 0.5 }} />
                    <Typography variant="caption" fontWeight="bold">Clicca per vedere chi</Typography>
                 </Box>
            ) : (
                <Typography variant="caption" color="success.main">Tutto in regola</Typography>
            )}
          </ModernCard>
        </Grid>
      </Grid>

      {/* 2. LAYOUT SPLIT AGENDA E AZIONI */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
            <SectionTitle>📅 Agenda di Oggi</SectionTitle>
            <Stack spacing={2}>
                {filteredBookings.length === 0 ? (
                    <ModernCard sx={{ alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
                        <EventBusyIcon sx={{ fontSize: 50, color: '#dfe6e9', mb: 2 }} />
                        <Typography color="text.secondary">Nessuna prenotazione per oggi.</Typography>
                    </ModernCard>
                ) : (
                    filteredBookings.map((booking) => {
                        const user = users.find(u => u.email === booking.email); 
                        const displayName = user ? (user.fullName || booking.email) : booking.email;
                        const oraInizio = booking.oraPrenotazione || "ND";
                        const nomeCorso = booking.tipoAttivita || "Allenamento";
                        
                        return (
                            <ModernCard key={booking.id} sx={{ flexDirection: 'row', alignItems: 'center', p: 3, height: 'auto', borderRadius: '16px' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, width: '100%' }}>
                                    <Box sx={{ 
                                        bgcolor: '#f3e5f5', color: '#8e24aa', 
                                        px: 2, py: 1, borderRadius: '12px', fontWeight: 'bold',
                                        display: 'flex', alignItems: 'center', gap: 1,
                                        minWidth: '100px', justifyContent: 'center'
                                    }}>
                                        <AccessTimeIcon fontSize="small" />
                                        {oraInizio}
                                    </Box>
                                    
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#2d3436' }}>
                                            {displayName}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#636e72' }}>
                                            Corso: {nomeCorso}
                                        </Typography>
                                    </Box>
                                </Box>
                            </ModernCard>
                        );
                    })
                )}
            </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
            <SectionTitle>⚡ Azioni Rapide</SectionTitle>
            <Stack spacing={2} sx={{ mb: 4 }}>
                <Button 
                    component={Link} to="/users/new"
                    variant="contained" startIcon={<PersonAddIcon />}
                    sx={{ py: 2, borderRadius: '12px', background: '#a32b2b', fontWeight: 'bold', boxShadow: '0 4px 14px 0 rgba(163, 43, 43, 0.39)', "&:hover": { background: '#802222' } }}
                >
                    Nuovo Utente
                </Button>
                <Button 
                    component={Link} to="/closure"
                    variant="outlined" startIcon={<EventBusyIcon />}
                    sx={{ py: 2, borderRadius: '12px', borderColor: '#a32b2b', color: '#a32b2b', fontWeight: 'bold', "&:hover": { borderColor: '#802222', bgcolor: '#fdf2f2' } }}
                >
                    Segna Chiusura
                </Button>
            </Stack>

            {expiredCertificates.length > 0 && (
                <>
                    <SectionTitle sx={{ color: '#c62828' }}>⚠️ Certificati Scaduti</SectionTitle>
                    <ModernCard sx={{ height: 'auto', p: 0, overflow: 'hidden' }}>
                        {expiredCertificates.slice(0, 3).map((cert, index) => {
                             const u = users.find(user => user.id === cert.userId) || {};
                             const uName = u.fullName || u.email || "Utente Sconosciuto";
                             return (
                                <Box key={cert.id} sx={{ p: 2, borderBottom: index < 2 ? '1px solid #f1f2f6' : 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', "&:hover": { bgcolor: '#fafafa' } }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">{uName}</Typography>
                                        <Typography variant="caption" color="error">
                                            Scaduto il: {cert.expiresAt.toDate ? format(cert.expiresAt.toDate(), 'dd/MM/yyyy') : 'ND'}
                                        </Typography>
                                    </Box>
                                </Box>
                             )
                        })}
                    </ModernCard>
                </>
            )}
        </Grid>
      </Grid>

      {/* --- MODALE CON TABS, RICERCA E LISTE --- */}
      <Dialog 
        open={openPopup} 
        onClose={() => setOpenPopup(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "24px", padding: "16px" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: "#2d3436", pb: 1 }}>
          {popupType === 'certs' ? "⚠️ Certificati Scaduti" : "📋 Gestione Abbonamenti"}
        </DialogTitle>
        
        {popupType === 'subs' && (
          <Tabs 
            value={tabIndex} 
            onChange={(e, val) => setTabIndex(val)} 
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
          >
            <Tab label={`In Scadenza (${expiringUsers.length})`} sx={{ fontWeight: 'bold' }} />
            <Tab label={`Attivi (${activeUsers.length})`} sx={{ fontWeight: 'bold' }} />
            <Tab label={`Scaduti (${expiredUsers.length})`} sx={{ fontWeight: 'bold' }} />
          </Tabs>
        )}

        {/* MODIFICA: Campo di ricerca live in cima al contenuto */}
        <Box sx={{ px: 1, mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Cerca per nome o email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchQuery("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
              sx: { borderRadius: "12px", bgcolor: "#f8f9fa", "& fieldset": { borderColor: "#e0e0e0" } }
            }}
          />
        </Box>

        <DialogContent sx={{ p: 1, maxHeight: '400px', overflowY: 'auto' }}>
          <List sx={{ pt: 0 }}>
            {(() => {
              let listToRender = [];
              if (popupType === 'certs') {
                listToRender = expiredCertificates.map(cert => {
                  const u = users.find(user => user.id === cert.userId) || {};
                  return { ...u, certExpiry: cert.expiresAt };
                });
              } else {
                if (tabIndex === 0) listToRender = expiringUsers;
                else if (tabIndex === 1) listToRender = activeUsers;
                else listToRender = expiredUsers;
              }

              // MODIFICA: Filtro per ricerca su Nome o Email (case insensitive)
              if (searchQuery.trim() !== "") {
                const queryLower = searchQuery.toLowerCase().trim();
                listToRender = listToRender.filter(u => {
                  const nameMatch = (u.fullName || "").toLowerCase().includes(queryLower);
                  const emailMatch = (u.email || "").toLowerCase().includes(queryLower);
                  return nameMatch || emailMatch;
                });
              }

              if (listToRender.length === 0) {
                return (
                  <Typography sx={{ py: 4, textAlign: 'center', color: '#b2bec3', fontWeight: 500 }}>
                    {searchQuery ? `Nessun risultato per "${searchQuery}"` : "Nessun utente in questa lista."}
                  </Typography>
                );
              }

              return listToRender.map((u, index) => {
                const expiry = getExpiryDate(u);
                return (
                  <React.Fragment key={u.id || index}>
                    <ListItem 
                      alignItems="flex-start" 
                      sx={{ px: 1, py: 1.5 }}
                      secondaryAction={
                        <IconButton component={Link} to={`/users/${u.id}`} size="small">
                          <ArrowForwardIosIcon fontSize="inherit" />
                        </IconButton>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: popupType === 'certs' ? "#ffebee" : "#f3e5f5", color: popupType === 'certs' ? "#d32f2f" : "#8e24aa" }}>
                          {(u.fullName || u.email || "U").charAt(0).toUpperCase()}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography fontWeight="700">{u.fullName || "Utente senza nome"}</Typography>}
                        secondary={
                          <Box component="span">
                            <Typography variant="caption" display="block" color="text.secondary">{u.email}</Typography>
                            {popupType === 'subs' && expiry && (
                              <Typography 
                                variant="caption" 
                                color={tabIndex === 2 ? "error" : tabIndex === 0 ? "warning.main" : "success.main"} 
                                fontWeight="bold"
                              >
                                {tabIndex === 2 ? "Scaduto il: " : "Scade il: "} 
                                {format(expiry, 'dd/MM/yyyy')}
                                {u.tipoAbbonamento === 'Carnet' && ` (${u.ingressiResidui || 0} ingressi)`}
                              </Typography>
                            )}
                            {popupType === 'certs' && u.certExpiry && (
                              <Typography variant="caption" color="error" fontWeight="bold">
                                Certificato scaduto il: {format(u.certExpiry.toDate ? u.certExpiry.toDate() : new Date(u.certExpiry), 'dd/MM/yyyy')}
                              </Typography>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < listToRender.length - 1 && <Divider variant="inset" component="li" sx={{ ml: "72px" }} />}
                  </React.Fragment>
                );
              });
            })()}
          </List>
          <Button 
            onClick={() => setOpenPopup(false)} 
            fullWidth 
            variant="contained" 
            sx={{ mt: 2, borderRadius: '12px', bgcolor: '#2d3436', textTransform: 'none', fontWeight: 'bold', py: 1.5, "&:hover": { bgcolor: '#000' } }}
          >
            Chiudi
          </Button>
        </DialogContent>
      </Dialog>
    </MainContainer>
  );
}